import { FunctionComponent, useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import TransactionHistoryTable from './TransactionHistory/TransactionHistoryTable';
import ElinxHistoryTable from './ElinxHistory/ElinxHistoryTable';
import BankVerifyHistoryTable from './BankVerifyHistory/BankVerifyHistoryTable';
import ScheduledTransactionHistory from './ScheduledTransactionHistory/ScheduledTransactionHistoryTable';
import ScheduledClientAccountTransfersTable from './ScheduledClientAccountTransfers/ScheduledClientAccountTransfersTable';
import { useTranslation } from 'react-i18next';

export type ActivityTabType =
  | 'Transaction History'
  | 'eLinx History'
  | 'Bank Verify History'
  | 'Scheduled Transactions'
  | 'Scheduled Client Account Transfers';

type ActivityTabProps = {
  active: boolean;
  accountID: string;
};

const ActivityTab: FunctionComponent<ActivityTabProps> = ({ active, accountID }) => {
  const history = useHistory();
  const transactionsFeatureFlags = useModuleFeatureFlag('transactions');
  const statementsFeatureFlags = useModuleFeatureFlag('statements');
  const elinxFeatureFlags = useModuleFeatureFlag('eLinx');
  const clientAccountsFeatureFlags = useModuleFeatureFlag('clientAccounts');
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.ActivityTab' });

  const activityTypeOptions = useMemo(() => {
    return [
      {
        label: t('transactionHistory', { defaultValue: 'Transaction History' }),
        value: 'Transaction History',
        visible: statementsFeatureFlags.ViewTransactionHistory,
      },
      {
        label: t('elinxHistory', { defaultValue: 'eLinx History' }),
        value: 'eLinx History',
        visible: elinxFeatureFlags.ViewPaylinkHitory,
      },
      {
        label: t('bankVerifyHistory', { defaultValue: 'Bank Verify History' }),
        value: 'Bank Verify History',
        visible: statementsFeatureFlags.ViewBankVerify,
      },
      {
        label: t('scheduledTransactions', { defaultValue: 'Scheduled Transactions' }),
        value: 'Scheduled Transactions',
        visible: transactionsFeatureFlags.ViewScheduledTransactions,
      },
      {
        label: t('scheduledClientAccountTransfers', { defaultValue: 'Scheduled Client Account Transfers' }),
        value: 'Scheduled Client Account Transfers',
        visible: clientAccountsFeatureFlags.isEnabled && clientAccountsFeatureFlags.ViewClientAccounts,
      },
    ].filter((option) => option.visible);
  }, [transactionsFeatureFlags, statementsFeatureFlags]);

  const [currentType, setCurrentType] = useState<ActivityTabType>(
    (queryParams.get('activityType') as ActivityTabType) || activityTypeOptions[0].label,
  );

  const handleCurrentType = (type: ActivityTabType) => {
    setCurrentType(type);
    const searchParams = new URLSearchParams(location.search);
    const params = [
      'startDate',
      'endDate',
      'status',
      'flagged',
      'type',
      'currency',
      'searchTerm',
      'timeZone',
      'eLinxType',
      'verificationStatus',
      'scheduleType',
      'transactionType',
      'frequency',
    ];

    params.forEach((param) => searchParams.delete(param));

    history.replace({
      search: searchParams.toString(),
    });
  };

  useEffect(() => {
    return () => history.replace(location.pathname);
  }, []);

  const getActivityTables = useCallback(() => {
    const [activityTables, setActivityTables] = useState(
      <>
        {currentType === 'Transaction History' && (
          <TransactionHistoryTable
            active={active}
            accountID={accountID}
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
        )}
        {currentType === 'eLinx History' && (
          <ElinxHistoryTable
            accountID={accountID}
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
        )}
        {currentType === 'Bank Verify History' && (
          <BankVerifyHistoryTable
            accountID={accountID}
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
        )}
        {currentType === 'Scheduled Transactions' && (
          <ScheduledTransactionHistory
            accountID={accountID}
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
        )}
        {currentType === 'Scheduled Client Account Transfers' && (
          <ScheduledClientAccountTransfersTable
            accountID={accountID}
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
        )}
      </>,
    );

    useEffect(() => {
      setActivityTables(
        <>
          {currentType === 'Transaction History' && (
            <TransactionHistoryTable
              active={active}
              accountID={accountID}
              activityTypeOptions={activityTypeOptions}
              currentType={currentType}
              handleCurrentType={handleCurrentType}
            />
          )}
          {currentType === 'eLinx History' && (
            <ElinxHistoryTable
              accountID={accountID}
              activityTypeOptions={activityTypeOptions}
              currentType={currentType}
              handleCurrentType={handleCurrentType}
            />
          )}
          {currentType === 'Bank Verify History' && (
            <BankVerifyHistoryTable
              accountID={accountID}
              activityTypeOptions={activityTypeOptions}
              currentType={currentType}
              handleCurrentType={handleCurrentType}
            />
          )}
          {currentType === 'Scheduled Transactions' && (
            <ScheduledTransactionHistory
              accountID={accountID}
              activityTypeOptions={activityTypeOptions}
              currentType={currentType}
              handleCurrentType={handleCurrentType}
            />
          )}
          {currentType === 'Scheduled Client Account Transfers' && (
            <ScheduledClientAccountTransfersTable
              accountID={accountID}
              activityTypeOptions={activityTypeOptions}
              currentType={currentType}
              handleCurrentType={handleCurrentType}
            />
          )}
        </>,
      );
    }, [currentType]);

    return activityTables;
  }, [currentType]);

  return <>{getActivityTables()}</>;
};
export default ActivityTab;
